/**
 * Audits By Date Model - Encapsulates audits by date API response data
 * This model handles audits by date data transformation and provides clean interface for UI components
 */

interface AuditItem {
  audit_id: string;
  audit_location?: {
    location_key: string;
    location_name: string;
    location_type: string;
  } | null;
  shift_start_date?: string | null;
  shift_start_time: string | null;
  shift_end_date?: string | null;
  shift_end_time: string | null;
  operator_name: string;
  time: string;
  is_autogenerated?: boolean;
  receipt_audit_queue_id?: string | null;
  previous_audit_id?: string | null;
  previous_receipt_audit_queue_id?: string | null;
  previous_audit_time?: string | null;
}

interface PosReceiptItem {
  audit_id?: string | null;
  window_relation?: 'before_period' | 'in_period' | 'after_period';
  receipt_audit_queue_id: string;
  vending_sales_id: string;
  pjr_report_id?: number | null;
  transaction_event_id?: number | null;
  report_type: string;
  location_key?: string | null;
  report_date?: string | null;
  report_time?: string | null;
  status?: string | null;
  scratcher_count: number;
  scratcher_amount: number;
  draw_game_count: number;
  draw_game_amount: number;
}

interface AuditsByDateServerData {
  audits?: AuditItem[];
  pos_receipts?: PosReceiptItem[];
  total?: number;
}

class AuditsByDateModel {
  private _serverData: AuditsByDateServerData | null;

  constructor(serverData: AuditsByDateServerData | null = null) {
    this._serverData = serverData;
  }

  // ===== BASIC PROPERTIES =====

  /**
   * Get the list of audits
   * @returns Array of audit items
   */
  private get audits(): AuditItem[] {
    return this._serverData?.audits || [];
  }

  // ===== COMPUTED PROPERTIES =====

  /**
   * Check if there are any audits
   * @returns true if audits array has items, false otherwise
   */
  get hasAudits(): boolean {
    return this.audits.length > 0;
  }

  /**
   * Get all shifts with their audit IDs
   * @returns Array of shift data with audit IDs
   */
  getUniqueShifts(): Array<{
    startTime: string;
    endTime: string;
    auditId: string;
    locationLabel?: string | null;
  }> {
    return this.audits.map((audit) => ({
      startTime: audit.shift_start_time || '',
      endTime: audit.shift_end_time || '',
      auditId: audit.audit_id,
      locationLabel: audit.audit_location?.location_name ?? null,
    }));
  }

  /**
   * Get all audits with their full data including time
   * @returns Array of audit items with all fields including time
   */
  getAudits(): AuditItem[] {
    return this.audits;
  }

  getPosReceipts(): PosReceiptItem[] {
    return this._serverData?.pos_receipts || [];
  }

  /**
   * Get operators as a comma-separated string
   * @param selectedAuditId - Optional audit ID to filter operators
   * @returns Comma-separated string of operator names
   */
  getOperatorsString(selectedAuditId?: string): string {
    const operatorNamesSet = new Set<string>();

    this.audits.forEach((audit) => {
      if (selectedAuditId) {
        // Show only the operator for the selected audit
        if (audit.audit_id === selectedAuditId && audit.operator_name && audit.operator_name.trim()) {
          operatorNamesSet.add(audit.operator_name.trim());
        }
      } else {
        // Show all operators
        if (audit.operator_name && audit.operator_name.trim()) {
          operatorNamesSet.add(audit.operator_name.trim());
        }
      }
    });

    return Array.from(operatorNamesSet).join(', ');
  }

  // ===== STATIC METHODS =====

  /**
   * Create model instance from server data
   * @param serverData - Server response data
   * @returns AuditsByDateModel instance
   */
  static fromServerData(serverData: AuditsByDateServerData | null): AuditsByDateModel {
    return new AuditsByDateModel(serverData);
  }

  /**
   * Create empty model instance
   * @returns Empty AuditsByDateModel instance
   */
  static createEmpty(): AuditsByDateModel {
    return new AuditsByDateModel();
  }
}

export default AuditsByDateModel;
export type { AuditItem, PosReceiptItem, AuditsByDateServerData };
